import type { TableCopyMethod } from '@/analytics/enums/table.enums'
import type { TableEditResult } from '../schemas'
import { WarningTriangle } from '@/assets/icons'
import Tooltip from '@/components/ui/tooltip/Tooltip'
import TableMatchText from './TableMatchText'
import CellEvidence from './CellEvidence'
import CellContentEditor from './CellContentEditor'
import { createCellEditorState, cellEditorContent, cellEditorNeedsVerification } from '../editor/content'
import { useRef, useState } from 'react'
import { TableCell, type CellSegment } from '../schemas'
import type { TableCellDraft } from '../hooks/useTableCellDrafts'
import TableCellEditMenu from './TableCellEditMenu'

/** Text remains editable regardless of the column's query interpretation. */
export default function EditableCell({
  draft,
  onDraftChange,
  cell,
  label,
  canEdit,
  onCopy,
  allowFootnotes = false,
  hideActions = false,
  selected = false,
  saving,
  onSave,
  matterId,
  tableId,
  pending = false,
  questionRevision,
}: {
  draft: TableCellDraft | null
  onDraftChange: (draft: TableCellDraft | null) => void
  matterId?: string
  tableId?: string
  pending?: boolean
  questionRevision?: number
  cell: TableCell | undefined
  allowFootnotes?: boolean
  label: string
  onCopy?: (footnotes: boolean, method?: TableCopyMethod) => Promise<void>
  hideActions?: boolean
  selected?: boolean
  canEdit: boolean
  saving: boolean
  onSave: (
    value: string,
    expectedValue: string,
    content: CellSegment[],
    expectedRevision?: string | null,
    clearVerification?: boolean,
    expectedQuestionRevision?: number
  ) => Promise<TableEditResult>
}) {
  const value = cell?.value ?? ''
  const notFound = Boolean(cell && cell.value === '' && cell.original_value !== null && !cell.edited_at && cell.segments.length === 0)
  // Only a user opening the editor should take focus; restoring a virtual row must not.
  const [focusEditor, setFocusEditor] = useState(false)
  const container = useRef<HTMLDivElement>(null)
  const saveInFlight = useRef(false)
  const references = cell?.references ?? []
  const startEdit = () => {
    if (!canEdit || saving) return
    const cellElement = container.current?.closest('td')
    const table = cellElement?.closest('table')
    const viewport = table?.parentElement
    if (cellElement && table?.tHead && viewport) {
      const hiddenHeight = table.tHead.getBoundingClientRect().bottom - cellElement.getBoundingClientRect().top
      if (hiddenHeight > 0) viewport.scrollTop -= hiddenHeight
    }
    setFocusEditor(true)
    const segments = cell?.segments ?? []
    const ids = [...new Set(segments.flatMap((segment) => segment.reference_ids))]
    onDraftChange({
      editorState: createCellEditorState(segments, cell?.needs_verification),
      references: [...references].sort((a, b) => ids.indexOf(a.id) - ids.indexOf(b.id)),
      expectedValue: value,
      expectedRevision: cell?.revision,
      expectedQuestionRevision: questionRevision,
    })
  }
  const save = async () => {
    if (draft === null || saveInFlight.current) return
    saveInFlight.current = true
    const content = cellEditorContent(draft.editorState.doc)
    try {
      const saved = await onSave(
        content.map((segment) => segment.text).join(''),
        draft.expectedValue,
        content,
        draft.expectedRevision,
        Boolean(cell?.needs_verification && !cellEditorNeedsVerification(draft.editorState.doc)),
        draft.expectedQuestionRevision
      )
      if (saved.ok || saved.conflict) onDraftChange(null)
    } finally {
      saveInFlight.current = false
    }
  }
  if (pending) {
    return (
      <div className="px-4 py-3 text-brand-neutral-400" title="Awaiting analysis" aria-label={`${label}: awaiting analysis`}>
        …
      </div>
    )
  }
  return (
    <div ref={container} className="min-w-0 px-4 py-3">
      {draft === null ? (
        <TableCellEditMenu
          allowFootnotes={allowFootnotes && Boolean(cell?.segments.some((segment) => segment.reference_ids.length > 0))}
          selected={selected}
          onCopy={onCopy}
          hideActions={hideActions}
          enabled={canEdit && !saving}
          onEdit={startEdit}
        >
          <div
            data-table-copy-content
            role={canEdit ? 'group' : undefined}
            tabIndex={canEdit && !saving ? 0 : undefined}
            aria-label={canEdit ? `Edit value for ${label}` : undefined}
            aria-description={canEdit ? 'Press Enter or Space to edit. Footnote links can be opened separately.' : undefined}
            aria-disabled={canEdit ? saving : undefined}
            onKeyDown={(event) => {
              if (!canEdit || saving || event.target !== event.currentTarget) return
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                startEdit()
              }
            }}
            className={`min-h-8 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-500 ${
              canEdit && !saving ? 'cursor-text' : ''
            }`}
          >
            <div>
              {notFound || (!value.trim() && !references.length) ? (
                <span className="text-brand-neutral-500" title={notFound ? 'Not found' : 'Empty'} aria-label={notFound ? 'Not found' : 'Empty'}>
                  —
                </span>
              ) : references.length > 0 && matterId ? (
                <CellEvidence
                  tableId={tableId}
                  segments={cell?.segments ?? []}
                  references={references}
                  matterId={matterId}
                  highlightOnHover={canEdit && !saving}
                />
              ) : (
                <span className={`whitespace-pre-wrap break-words rounded ${canEdit && !saving ? 'hover:bg-brand-neutral-100' : ''}`} title={value}>
                  <TableMatchText text={value} />
                </span>
              )}
              {cell?.needs_verification && (
                <span data-table-copy-ignore className="inline-flex align-baseline mx-0.5">
                  <Tooltip title="We couldn’t confirm a supporting passage. Check this answer against the document.">
                    <span
                      tabIndex={0}
                      role="img"
                      aria-label="Needs manual verification"
                      className="inline-flex items-center min-h-[18px] px-1 pt-0 pb-[1px] rounded border text-xs text-brand-neutral-500 bg-brand-neutral-200 border-brand-neutral-300"
                    >
                      <WarningTriangle className="size-3.5" aria-hidden="true" />
                    </span>
                  </Tooltip>
                </span>
              )}
            </div>
          </div>
        </TableCellEditMenu>
      ) : (
        <div className="flex flex-col gap-2">
          <CellContentEditor
            draft={draft}
            onDraftChange={onDraftChange}
            label={label}
            matterId={matterId}
            tableId={tableId}
            saving={saving}
            autoFocus={focusEditor}
            onSave={() => void save()}
            onCancel={() => {
              if (!saveInFlight.current) onDraftChange(null)
            }}
          />
        </div>
      )}
    </div>
  )
}
